import { useState, useEffect, useRef, useCallback } from 'react';
import type { IFormSchema } from '~/types';

const ADMIN_ORIGIN = import.meta.env.VITE_ADMIN_ORIGIN || 'http://localhost:3001';

interface UsePreviewModeResult {
  isPreviewMode: boolean;
  schema: IFormSchema | null;
  loading: boolean;
}

export const usePreviewMode = (): UsePreviewModeResult => {
  const isPreviewMode = new URLSearchParams(window.location.search).get('mode') === 'preview';
  const [schema, setSchema] = useState<IFormSchema | null>(null);
  const [loading, setLoading] = useState(isPreviewMode);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const sendToParent = useCallback(
    (type: string, payload: Record<string, unknown> = {}) => {
      if (!isPreviewMode || !window.parent || window.parent === window) return;
      window.parent.postMessage({ type, ...payload }, ADMIN_ORIGIN);
    },
    [isPreviewMode],
  );

  // Listen for schema updates from admin
  useEffect(() => {
    if (!isPreviewMode) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== ADMIN_ORIGIN) return;

      const { type } = event.data || {};

      if (type === 'preview:schema') {
        setSchema(event.data.schema as IFormSchema);
        setLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);

    // Signal readiness to admin
    sendToParent('preview:ready');

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isPreviewMode, sendToParent]);

  // Report height changes back to admin via ResizeObserver
  useEffect(() => {
    if (!isPreviewMode) return;

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.scrollHeight;
        sendToParent('formHeight', { height });
      }
    });

    resizeObserverRef.current.observe(document.body);

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [isPreviewMode, sendToParent]);

  return { isPreviewMode, schema, loading };
};
